import { useEffect, useMemo, useState } from "react";
import {
  GraduationCap,
  Sparkles,
  FileSpreadsheet,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Circle,
  Dot,
  AlertCircle,
  PlayCircle,
} from "lucide-react";
import Modal from "../../components/ui/Modal";
import { ApiService } from "../../services/api";
import { VideoEmbed } from "./TeachFlow";
import type { LessonWalkResponse, LessonWalkKazanim, TeachSession } from "../../types/navigator";

interface Props {
  lessonId: string;
  onClose: () => void;
  onStartTest: (testScope: { sectionId: string | null; count: number }, konu: string) => void;
}

interface Foy {
  sectionId: string;
  unit: string;
  konu: string;
  kazanimlar: LessonWalkKazanim[];
}

type KazState = "mastered" | "current" | "taught" | "pending";

export default function LessonWalk({ lessonId, onClose, onStartTest }: Props) {
  const [walk, setWalk] = useState<LessonWalkResponse | null>(null);
  const [foyIdx, setFoyIdx] = useState(0);
  const [kazIdx, setKazIdx] = useState(0);
  const [teach, setTeach] = useState<TeachSession | null>(null);
  const [teachLoading, setTeachLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [videoDone, setVideoDone] = useState(false);
  const [done, setDone] = useState(false);

  // Kazanımları föye (bölüm) göre grupla — sıra korunur
  const foyler = useMemo<Foy[]>(() => {
    if (!walk) return [];
    const out: Foy[] = [];
    const seen = new Map<string, number>();
    for (const k of walk.kazanimlar) {
      let g = seen.get(k.sectionId);
      if (g === undefined) {
        g = out.length;
        seen.set(k.sectionId, g);
        out.push({ sectionId: k.sectionId, unit: k.unit, konu: k.konu, kazanimlar: [] });
      }
      out[g].kazanimlar.push(k);
    }
    return out;
  }, [walk]);

  // Dersin kazanım dizisini çek, kaldığın FÖY + kazanıma git
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const w = await ApiService.getLessonWalk(lessonId);
        if (!alive) return;
        setWalk(w);
      } catch (e: any) {
        if (alive) setError(e?.message || "Ders yüklenemedi");
      }
    })();
    return () => {
      alive = false;
    };
  }, [lessonId]);

  // Walk gelince kaldığın yeri (resumeIndex → föy+kazanım) hesapla
  useEffect(() => {
    if (!walk || !foyler.length) return;
    const resumeGain = walk.kazanimlar[Math.min(walk.resumeIndex, walk.total - 1)] || walk.kazanimlar[0];
    if (!resumeGain) return;
    const fi = foyler.findIndex((f) => f.sectionId === resumeGain.sectionId);
    const foy = foyler[fi >= 0 ? fi : 0];
    const ki = foy.kazanimlar.findIndex((k) => k.gainId === resumeGain.gainId);
    setFoyIdx(fi >= 0 ? fi : 0);
    setKazIdx(ki >= 0 ? ki : 0);
    // eslint-disable-next-line
  }, [foyler]);

  const foy = foyler[foyIdx] || null;
  const current = foy?.kazanimlar[kazIdx] || null;

  // Mevcut kazanımın öğretim paketini (video + AI context + test kapsamı) çek
  useEffect(() => {
    if (!current) return;
    let alive = true;
    setVideoDone(false);
    (async () => {
      try {
        setTeachLoading(true);
        const t = await ApiService.startTeach({ gainId: current.gainId });
        if (alive) setTeach(t);
      } catch {
        if (alive) setTeach(null);
      } finally {
        if (alive) setTeachLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line
  }, [current?.gainId]);

  const markVideo = async () => {
    if (!current || videoDone) return;
    setVideoDone(true);
    try {
      await ApiService.recordTaught(current.gainId, { video: true });
    } catch {
      /* geç */
    }
  };

  const startAI = async () => {
    if (!teach) return;
    try {
      await ApiService.recordTaught(teach.kazanim.gainId, { ai: true });
    } catch {
      /* geç */
    }
    window.dispatchEvent(new CustomEvent("toggleAITutorPanel", { detail: { context: teach.aiContext } }));
    onClose();
  };

  const startTest = async () => {
    if (!teach) return;
    await markVideo();
    onStartTest(teach.testScope, teach.kazanim.konu);
    onClose();
  };

  const goNextKazanim = () => {
    if (!foy) return;
    if (kazIdx < foy.kazanimlar.length - 1) {
      setKazIdx((i) => i + 1);
    } else {
      goNextFoy();
    }
  };

  const goNextFoy = () => {
    if (foyIdx < foyler.length - 1) {
      setFoyIdx((i) => i + 1);
      setKazIdx(0);
    } else {
      setDone(true);
    }
  };

  const goPrevFoy = () => {
    if (foyIdx > 0) {
      setFoyIdx((i) => i - 1);
      setKazIdx(0);
    }
  };

  const kazState = (k: LessonWalkKazanim, isCurrent: boolean): KazState => {
    if (k.mastered) return "mastered";
    if (isCurrent) return "current";
    if (k.taught) return "taught";
    return "pending";
  };

  // Föy-içi ilerleme + genel ilerleme
  const foyMastered = foy ? foy.kazanimlar.filter((k) => k.mastered).length : 0;
  const foyTotal = foy ? foy.kazanimlar.length : 0;
  const foyPct = foyTotal ? Math.round((foyMastered / foyTotal) * 100) : 0;
  const title = walk ? `Ders anlatımı · ${walk.lessonTitle}` : "Ders anlatımı";

  return (
    <Modal isOpen onClose={onClose} title={title} size="lg" closeOnBackdrop={false}>
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertCircle className="h-5 w-5 shrink-0" />
          {error}
        </div>
      )}

      {!walk && !error && (
        <div className="grid place-items-center py-16">
          <div className="h-9 w-9 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      )}

      {done && (
        <div className="py-6 text-center">
          <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-emerald-100 text-emerald-600">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <p className="mb-1 text-lg font-semibold text-gray-900">Bu dersi baştan sona gezdin! 🎉</p>
          <p className="mx-auto mb-4 max-w-sm text-sm text-gray-600">
            Koçun defterine işledi. İstediğin zaman "Bana dersi anlat" ile kaldığın yerden pekiştirmeye
            devam edebilirsin.
          </p>
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            Haritama dön
          </button>
        </div>
      )}

      {walk && !done && foy && current && (
        <div className="space-y-4">
          {/* Föy başlığı + ilerleme */}
          <div>
            <div className="mb-1.5 flex items-center justify-between text-xs">
              <span className="font-semibold text-gray-800">
                {foy.unit} · {foy.konu}
              </span>
              <span className="text-gray-500">
                Föy {foyIdx + 1}/{foyler.length}
                {walk.progress.total ? ` · genel ${walk.progress.mastered}/${walk.progress.total} pekişti` : ""}
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
              <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${foyPct}%` }} />
            </div>
            <div className="mt-1 text-[11px] text-gray-400">
              Bu konuda {foyMastered}/{foyTotal} kazanım pekişti
            </div>
          </div>

          {/* Föy kazanım checklist'i */}
          <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-2">
            <div className="max-h-40 space-y-0.5 overflow-y-auto">
              {foy.kazanimlar.map((k, i) => {
                const st = kazState(k, i === kazIdx);
                return (
                  <button
                    key={k.gainId}
                    onClick={() => setKazIdx(i)}
                    className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors ${
                      i === kazIdx ? "bg-blue-50 ring-1 ring-blue-200" : "hover:bg-white"
                    }`}
                  >
                    {st === "mastered" ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                    ) : st === "current" ? (
                      <Dot className="h-4 w-4 shrink-0 text-blue-600" />
                    ) : st === "taught" ? (
                      <Circle className="h-4 w-4 shrink-0 fill-amber-100 text-amber-500" />
                    ) : (
                      <Circle className="h-4 w-4 shrink-0 text-gray-300" />
                    )}
                    <span className={`min-w-0 flex-1 truncate ${st === "mastered" ? "text-gray-400 line-through" : "text-gray-700"}`}>
                      {k.gainText}
                    </span>
                    {st === "taught" && <span className="shrink-0 text-[10px] font-medium text-amber-600">anlatıldı</span>}
                    {st === "mastered" && <span className="shrink-0 text-[10px] font-medium text-emerald-600">pekişti</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Aktif kazanım */}
          <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <GraduationCap className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
            <div className="min-w-0">
              <p className="mb-0.5 text-[11px] font-medium text-blue-700">
                Kazanım {kazIdx + 1}/{foy.kazanimlar.length}
              </p>
              <p className="text-sm font-semibold text-gray-900">{current.gainText}</p>
            </div>
          </div>

          {/* Atom: video + aksiyonlar */}
          {teachLoading ? (
            <div className="grid place-items-center py-10">
              <div className="h-7 w-7 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
          ) : teach?.video?.url ? (
            <div>
              <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <PlayCircle className="h-4 w-4 text-blue-600" />
                Senin için bu videoyu seçtim{teach.video.startTime ? ` (${teach.video.startTime}–${teach.video.endTime})` : ""} — otur izle
              </div>
              <VideoEmbed url={teach.video.url} startTime={teach.video.startTime} endTime={teach.video.endTime} />
              <button
                onClick={markVideo}
                disabled={videoDone}
                className={`mt-2 inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                  videoDone
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                <CheckCircle2 className="h-4 w-4" /> {videoDone ? "İzledim ✓" : "İzledim"}
              </button>
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
              Bu kazanım için hazır video yok — AI öğretmenle çalışabilir ya da doğrudan sıradakine geçebilirsin.
            </div>
          )}

          {/* Aksiyonlar */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button
              onClick={startAI}
              disabled={!teach}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              <Sparkles className="h-4 w-4" /> AI öğretmenle çalış
            </button>
            <button
              onClick={startTest}
              disabled={!teach}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:border-blue-300 hover:bg-gray-50 disabled:opacity-50"
            >
              <FileSpreadsheet className="h-4 w-4 text-blue-600" /> Mini test
            </button>
          </div>

          {/* Gezinme: föy içi kazanım + föyler arası */}
          <div className="flex items-center justify-between gap-2 border-t border-gray-100 pt-3">
            <button
              onClick={() => setKazIdx((i) => Math.max(0, i - 1))}
              disabled={kazIdx === 0}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" /> Önceki
            </button>

            <div className="flex items-center gap-2">
              {foyIdx > 0 && (
                <button
                  onClick={goPrevFoy}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50"
                >
                  Önceki föy
                </button>
              )}
              {kazIdx >= foy.kazanimlar.length - 1 ? (
                <button
                  onClick={goNextFoy}
                  className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                >
                  {foyIdx >= foyler.length - 1 ? "Dersi bitir" : "Sıradaki föy"} <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={goNextKazanim}
                  className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                >
                  Sıradaki kazanım <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
